import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext';
import {
    getLiteTradeAssetsBySymbol,
    makeSelectLiteTradeWidgetByWidgetKeyAndCollectionKey,
} from './liteTradeSelectors';
import { LITE_TRADE_ASSETS_CONFIG } from './liteTradeAssetsConstants';
import { logAppsflyerAction } from '../../app/appActions';
import { LOG_AF_EVENT_NAMES } from '../../app/appConstants';
import { getUserSelectedAccountType } from '../../app/appSelectors';
import { ILiteTradeAssetSymbol, ILiteTradeWidgetsItem } from './types/liteTradeReducer.t';
import { locationSlice } from '../Location/locationReducer';
import { LOCATION_NAMES } from '../Location/locationConstants';
import { getIsLocationPlatformDesktopSelector } from '../Location/locationSelectors';

export const useGetAssetTitleBySymbolCallback = () => {
    const { getLangPath } = useLangContext();
    const userAssetsMap = useSelector(getLiteTradeAssetsBySymbol);

    return useCallback((symbol: ILiteTradeAssetSymbol) => {
        const langAssetTitle = getLangPath(`lite_version.assets.${symbol}.title`, {}, { emptyDefault: true });
        const dxDescription = userAssetsMap[symbol]?.info?.description;
        return langAssetTitle || dxDescription || '';
    }, [getLangPath, userAssetsMap]);
};

export const useLiteTradeAsset = (symbol: ILiteTradeAssetSymbol) => {
    const { getLangPath } = useLangContext();
    const userAssetsMap = useSelector(getLiteTradeAssetsBySymbol);
    const dispatch = useDispatch();
    const selectedAccountType = useSelector(getUserSelectedAccountType);
    const getAssetTitleBySymbol = useGetAssetTitleBySymbolCallback();
    const accountType = useSelector(getUserSelectedAccountType);
    const isPlatformDesktop = useSelector(getIsLocationPlatformDesktopSelector);
    const title = useMemo(() => {
        return getAssetTitleBySymbol(symbol);
    }, [getAssetTitleBySymbol, symbol]);

    const description = useMemo(() => {
        const langAssetDescription = getLangPath(`lite_version.assets.${symbol}.description`, {}, { emptyDefault: true });
        const dxDescription = userAssetsMap[symbol]?.info?.description;
        return langAssetDescription || dxDescription || '';
    }, [getLangPath, symbol, userAssetsMap]);

    const openAssetSlide = useCallback((collectionKey = '', widgetKey = '') => {
        dispatch(locationSlice.actions.openLocationAction({
            name: LOCATION_NAMES.ASSET,
            searchParams: { symbol },
        }));
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.MARKET_ASSET_CLICK,
            payload: {
                account_type: selectedAccountType,
                collection_key: collectionKey,
                widget_key: widgetKey,
            },
        }));
        if (isPlatformDesktop) {
            dispatch(logAppsflyerAction({
                name: LOG_AF_EVENT_NAMES.ASSET_TAB_OPEN,
                payload: {
                    account_type: accountType,
                    asset: symbol,
                    collection_key: collectionKey,
                    source: 'market',
                },
            }));
        }
    }, [accountType, isPlatformDesktop, dispatch, selectedAccountType, symbol]);

    const configParams = LITE_TRADE_ASSETS_CONFIG[symbol] || LITE_TRADE_ASSETS_CONFIG['default'];
    const isAssetConfigDefault = !LITE_TRADE_ASSETS_CONFIG[symbol]

    return { openAssetSlide, title, description, isAssetConfigDefault, ...configParams };
};

export const useLiteTradeCollectionWidget = (widgetKey: ILiteTradeWidgetsItem['widget_key'], collectionKey: ILiteTradeWidgetsItem['collection_key']) => {
    const widgetData = useSelector(makeSelectLiteTradeWidgetByWidgetKeyAndCollectionKey(widgetKey, collectionKey));

    const params = useMemo(() => {
        try {
            return JSON.parse(widgetData.params);
        } catch (e) {
            return {};
        }
    }, [widgetData]);

    return { params };
};
